<template>
  <div class="tw-flex tw-gap-2">
    <CustomerActionButton
      v-for="action in visibleActions"
      :key="action.label"
      @click="action.disabled ? null : action.onClick()"
      :tooltip="action.label"
      :class="{
        'tw-opacity-50 !tw-cursor-not-allowed': action.disabled,
        'tw-cursor-pointer': !action.disabled,
      }"
      v-tooltip="action.tooltip"
    >
      <component
        :is="action.icon"
        v-if="action.icon"
        class="tw-w-4 tw-h-4"
        :class="action.iconClass"
      />
    </CustomerActionButton>

    <!-- Confirmation Modal -->
    <ConfirmationModal
      v-model="showConfirmationModal"
      :title="t('common.forms.confirmation').toUpperCase()"
      :message="confirmationMessage"
      :type="confirmationModalType"
      :confirm-text="t('common.forms.confirm')"
      :cancel-text="t('common.forms.cancel')"
      @confirm="onConfirmAction"
      @cancel="onCloseModal"
    />

    <!-- Request clarification modal -->
    <GenericModal
      v-model="showRequestClarificationModal"
      :title="t('orders.titles.confirm_clarification')"
    >
      <template #body>
        <div class="tw-mb-4">
          <Textarea
            v-model="clarificationComment"
            class="tw-border tw-w-full tw-px-3 tw-py-2 tw-rounded tw-resize-none"
            :placeholder="t('orders.titles.message_hint')"
            rows="6"
          ></Textarea>
        </div>
      </template>

      <template #footer>
        <div class="tw-flex tw-justify-end tw-gap-2 tw-items-center">
          <Button
            class="tw-bg-white tw-border-solid tw-border-2 tw-border-secondary-500 tw-text-secondary-500 hover:tw-bg-secondary-500 hover:tw-text-white tw-px-2 tw-py-2 tw-rounded-md tw-text-sm tw-flex tw-gap-2 tw-transition-colors"
            type="button"
            @click="onCloseRequestClarificationModal"
          >
            {{ t('common.forms.cancel') }}
          </Button>
          <Button
            class="tw-bg-secondary-500 tw-border-2 tw-border-secondary-500 tw-text-white hover:tw-bg-secondary-600 tw-border-solid tw-px-2 tw-py-2 tw-rounded-md tw-text-sm tw-flex tw-gap-2 tw-transition-colors"
            :class="{
              'tw-opacity-50 !tw-cursor-not-allowed': disableActions || !clarificationComment,
            }"
            type="button"
            @click="performClarificationAction"
            :disabled="disableActions || !clarificationComment"
          >
            {{ t('requests.actions.request_clarification') }}
          </Button>
        </div>
      </template>
    </GenericModal>
  </div>
</template>
<script setup lang="ts">
import { useApiUtils } from '@/_helpers/composables/api-utils.composable';
import CheckTextIcon from '@/assets/icons/CheckText.svg';
import ApproveIcon from '@/assets/icons/Approve.svg';
import CloseIcon from '@/assets/icons/Close.svg';
import EditIcon from '@/assets/icons/Edit.svg';
import ForwardIcon from '@/assets/icons/Forward.svg';
import QuestionIcon from '@/assets/icons/Question.svg';
import DeleteIcon from '@/assets/icons/Trash.svg';
import ConfirmationModal from '@/components/shared/ConfirmationModal.vue';
import CustomerActionButton from '@/components/shared/CustomerActionButton.vue';
import GenericModal from '@/components/shared/GenericModal.vue';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useCustomerStore } from '@/stores/customer.store';
import { useRecurringInstructionStore } from '@/stores/recurring-instructions.store';
import { useUserStore } from '@/stores/user.store';
import { storeToRefs } from 'pinia';
import { computed, shallowRef, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import router from '@/router';

const { currentUser } = storeToRefs(useUserStore());
const { handleApiError } = useApiUtils();
const { t } = useI18n();

const {
  approveRecurringInstruction,
  rejectRecurringInstruction,
  requestRecurringInstructionClarification,
  respondToRecurringInstructionClarification,
  bulkRecurringInstructionAction,
  deleteRecurringInstruction,
} = useRecurringInstructionStore();

// Action type enum
enum ActionType {
  DELETE = 'delete',
  EDIT = 'edit',
  APPROVE = 'approve',
  REJECT = 'reject',
  CLARIFICATION = 'clarification',
  MARK_CLARIFICATIONS_COMPLETED = 'mark_clarifications_completed',
  SUBMIT = 'submit',
}

const customerStore = useCustomerStore();
const disableActions = computed(() => customerStore.shouldDisableCustomerActions());

const showConfirmationModal = ref(false);
const confirmationMessage = ref('');
const currentActionType = ref<ActionType | null>(null);

// Request clarification modal
const showRequestClarificationModal = ref(false);
const clarificationComment = ref('');
const confirmationModalType = ref<'warning' | 'danger' | 'info' | 'success'>('warning');

const performSubmitAction = async () => {
  await handleApiError(async () => {
    await bulkRecurringInstructionAction('submit', [props.order.id]);
    emit('reloadTable');
  }, t('orders.titles.successful_submit'));
};

const performApproveAction = async () => {
  await handleApiError(async () => {
    await approveRecurringInstruction(props.order.id);
    emit('reloadTable');
  }, t('orders.titles.successful_approval'));
};

const performRejectAction = async () => {
  await handleApiError(async () => {
    await rejectRecurringInstruction(props.order.id);
    emit('reloadTable');
  }, t('orders.titles.successful_rejection'));
};

const performDeleteAction = async () => {
  await handleApiError(async () => {
    await deleteRecurringInstruction(props.order.id);
    emit('reloadTable');
  }, t('orders.titles.successful_delete'));
};

const performClarificationAction = async () => {
  console.log('performClarificationAction');
  await handleApiError(async () => {
    await requestRecurringInstructionClarification(props.order.id, clarificationComment.value);
    emit('reloadTable');
    onCloseRequestClarificationModal();
    clarificationComment.value = '';
  }, t('orders.titles.successful_clarification'));
};

const onCloseRequestClarificationModal = () => {
  showRequestClarificationModal.value = false;
  clarificationComment.value = '';
};

const openRequestClarificationModal = () => {
  currentActionType.value = ActionType.CLARIFICATION;
  showRequestClarificationModal.value = true;
};

const respondToClarification = async () => {
  await handleApiError(async () => {
    await respondToRecurringInstructionClarification(props.order.id);
    emit('reloadTable');
  }, t('orders.titles.successful_clarification_response'));
};

const actionMap = {
  [ActionType.SUBMIT]: performSubmitAction,
  [ActionType.APPROVE]: performApproveAction,
  [ActionType.REJECT]: performRejectAction,
  [ActionType.DELETE]: performDeleteAction,
  [ActionType.CLARIFICATION]: performClarificationAction,
  [ActionType.MARK_CLARIFICATIONS_COMPLETED]: respondToClarification,
};

const actionTypeToMessageMap = {
  [ActionType.SUBMIT]: t('orders.titles.confirm_forward'),
  [ActionType.APPROVE]: t('orders.titles.confirm_approval'),
  [ActionType.REJECT]: t('orders.titles.confirm_rejection'),
  [ActionType.DELETE]: t('orders.titles.confirm_delete'),
  [ActionType.CLARIFICATION]: t('orders.titles.confirm_mark_clarifications_completed'),
};

const actionTypeToModalTypeMap = {
  [ActionType.SUBMIT]: 'info',
  [ActionType.APPROVE]: 'success',
  [ActionType.REJECT]: 'warning',
  [ActionType.DELETE]: 'danger',
  [ActionType.MARK_CLARIFICATIONS_COMPLETED]: 'info',
} as const;

const openConfirmationModal = (actionType: ActionType) => {
  confirmationModalType.value = actionTypeToModalTypeMap[actionType] || 'warning';
  currentActionType.value = actionType;
  confirmationMessage.value = actionTypeToMessageMap[actionType];
  showConfirmationModal.value = true;
};

const onConfirmAction = async () => {
  if (currentActionType.value === null) return;
  await actionMap[currentActionType.value]();
};

const onCloseModal = () => {
  showConfirmationModal.value = false;
};

const props = defineProps<{
  order: any;
}>();

const emit = defineEmits<{
  reloadTable;
}>();

const showApproveOrRejectAction = () => {
  if (currentUser.value?.user_role.includes('Admin')) {
    return ['PENDING_APPROVAL', 'CLEARING', 'CLARIFICATION_REQUESTED'].includes(
      props.order?.status_display
    );
  }
  if (currentUser.value?.user_role.includes('Approver')) {
    return props.order?.status_display === 'PENDING_APPROVAL';
  }
  if (currentUser.value?.user_role.includes('Order Clearer')) {
    return ['PENDING_APPROVAL', 'CLARIFICATION_REQUESTED'].includes(props.order?.status_display);
  }
  return false;
};

const showRequestClarificationAction = () => {
  if (
    currentUser.value?.user_role.includes('Admin') ||
    currentUser.value?.user_role.includes('Approver') ||
    currentUser.value?.user_role.includes('Order Clearer')
  ) {
    return (
      ['PENDING_APPROVAL', 'CLEARING'].includes(props.order?.status_display) &&
      !props.order?.is_clarification_requested
    );
  }
  return false;
};

const showEditOrderAction = () =>
  (props.order.status === 'DRAFT' ||
    (props.order.status !== 'APPROVED' &&
      props.order.status !== 'REJECTED' &&
      props.order.is_clarification_requested)) &&
  props.order.created_by_id === currentUser.value?.id;

const showRespondToClarificationAction = () => {
  if (['APPROVED', 'REJECTED'].includes(props.order.status)) {
    return false;
  }
  return (
    props.order.is_clarification_requested &&
    (currentUser.value?.id === props.order.created_by_id ||
      currentUser.value?.id === props.order.requested_clarification_by_id)
  );
};

const actions = shallowRef<any[]>([
  {
    label: t('common.forms.submit'),
    icon: ForwardIcon,
    iconClass: '!tw-text-blue-500',
    onClick: () => {
      openConfirmationModal(ActionType.SUBMIT);
    },
    show: () => {
      return props.order.status === 'DRAFT';
    },
    disabled: disableActions.value,
    tooltip: disableActions.value
      ? {
          theme: 'inocu-tooltip',
          content: t('common.message.customer_archived_tooltip'),
        }
      : null,
  },
  {
    label: t('requests.actions.approve'),
    icon: ApproveIcon,
    iconClass: 'tw-text-green-500',
    onClick: () => {
      openConfirmationModal(ActionType.APPROVE);
    },
    show: () => {
      return showApproveOrRejectAction();
    },
    disabled: disableActions.value,
    tooltip: disableActions.value
      ? {
          theme: 'inocu-tooltip',
          content: t('common.message.customer_archived_tooltip'),
        }
      : null,
  },
  {
    label: t('requests.actions.request_clarification'),
    icon: QuestionIcon,
    iconClass: 'tw-text-yellow-500',
    onClick: () => {
      openRequestClarificationModal();
    },
    show: () => {
      return showRequestClarificationAction();
    },
    disabled: disableActions.value,
    tooltip: disableActions.value
      ? {
          theme: 'inocu-tooltip',
          content: t('common.message.customer_archived_tooltip'),
        }
      : null,
  },
  {
    label: t('orders.actions.mark_clarifications_completed'),
    icon: CheckTextIcon,
    iconClass: 'tw-text-green-500',
    onClick: () => {
      openConfirmationModal(ActionType.MARK_CLARIFICATIONS_COMPLETED);
    },
    show: () => {
      return showRespondToClarificationAction();
    },
    disabled: disableActions.value,
    tooltip: disableActions.value
      ? {
          theme: 'inocu-tooltip',
          content: t('common.message.customer_archived_tooltip'),
        }
      : null,
  },
  {
    label: t('requests.actions.reject'),
    icon: CloseIcon,
    iconClass: 'tw-text-red-500',
    onClick: () => {
      openConfirmationModal(ActionType.REJECT);
    },
    show: () => {
      return showApproveOrRejectAction();
    },
    disabled: disableActions.value,
    tooltip: disableActions.value
      ? {
          theme: 'inocu-tooltip',
          content: t('common.message.customer_archived_tooltip'),
        }
      : null,
  },
  {
    label: t('common.forms.edit'),
    icon: EditIcon,
    iconClass: 'tw-text-gray-500',
    onClick: () => {
      router.push({
        name: 'edit-recurring-order',
        params: { id: props.order.id },
      });
    },
    show: () => {
      return showEditOrderAction();
    },
    disabled: disableActions.value,
    tooltip: disableActions.value
      ? {
          theme: 'inocu-tooltip',
          content: t('common.message.customer_archived_tooltip'),
        }
      : null,
  },
  {
    label: t('common.forms.delete'),
    icon: DeleteIcon,
    iconClass: 'tw-text-red-500',
    onClick: () => {
      openConfirmationModal(ActionType.DELETE);
    },
    show: () => {
      return props.order.status === 'DRAFT' && props.order.created_by_id === currentUser.value?.id;
    },
    disabled: disableActions.value,
    tooltip: disableActions.value
      ? {
          theme: 'inocu-tooltip',
          content: t('common.message.customer_archived_tooltip'),
        }
      : null,
  },
]);

const visibleActions = computed(() => {
  return actions.value.filter(action => action.show());
});
</script>
